/// <reference path="../../app.definition.ts" />

import angular from "angular";
import * as jsonLogic from "json-logic-js";
import { ChartTypeEnum, StatisticsEnum } from "@common/model/analytics.model";
import { UtilityService } from "@common/service/util.service";
import { Util } from "@common/util";

export interface IChartService {
    getMinMaxData(data, field, type, min?, max?): object;
    processOptions(config, data);
    getStatistics(statisticConfig, data);
}

export class ChartService implements IChartService {
    static $inject = ["$filter", "UtilityService", "$timeout"];
    constructor(
        private $filter: ng.IFilterService,
        private $_util: UtilityService,
        private $timeout: ng.ITimeoutService
    ) {
    }
    chart;
    options;
    data;
    config;
    DATA_TYPE = {
        DATE: 'date',
        NUMBER: 'number',
        STRING: 'string'
    };
    CHART_DATA_TYPES = {
        date: 'time',
        number: 'value'
    };
    VALUE_OFFSET = 2;

    processOptions = (config, data) => {
        var objOpt;
        switch (config.type.toLowerCase()) {
            case "line":
                var options = config.options;
                objOpt = this.getDefaultLineOptions();
                var xAxis = options.xAxis;
                var yAxis = options.yAxis;
                var tooltipTemplate = options.tooltip.template;
                var obj = this.getMinMaxData(data, xAxis.field, xAxis.type);
                objOpt.xAxis.min = obj.min;
                objOpt.xAxis.max = obj.max;
                objOpt.series.data = data;
                this.setAxisLabel(xAxis, objOpt, "xAxis");
                this.setAxisLabel(yAxis, objOpt, "yAxis");

                objOpt.tooltip.formatter = (params) => {
                    var rowData = params.data;
                    var template = tooltipTemplate;
                    var valueMapping = options.tooltip['value-map'];
                    var emptyText = "<div style='width: 50px;text-align: center;display: inline-block;color: #59565B;'><b>-</b></div>";
                    if (Array.isArray(valueMapping)) {
                        valueMapping.forEach((vm) => {
                            var value = rowData[vm.field];
                            if (value != null && typeof value != "undefined") {
                                switch (vm.type) {
                                    case this.DATA_TYPE.DATE:
                                        value = this.formatDate(value, vm.format);
                                        break;
                                    case this.DATA_TYPE.NUMBER:
                                        if (value != 0 && vm.decimal) {
                                            value = parseFloat(value + "").toFixed(vm.decimal);
                                        }
                                        if (vm.prefix) {
                                            value = vm.prefix + value;
                                        }
                                        if (vm.suffix) {
                                            value = value + vm.suffix;
                                        }
                                        template = template.replace(vm.templateKey, value);
                                        break;
                                    default:
                                        value = rowData[vm.field];
                                        break;
                                }
                                if (typeof value == "string" && value.trim() == "") {
                                    template = template.replace(vm.templateKey, emptyText);
                                } else {
                                    template = template.replace(vm.templateKey, value);
                                }
                            } else {
                                template = template.replace(vm.templateKey, emptyText);
                            }
                        })
                    }

                    return template;
                }

                break;
        }
        if (config.options.showTrendLine) {
            objOpt.series.markLine = this.getTrendLineOptions(data, objOpt.xAxis.min, objOpt.xAxis.max);
        }
        if (window.innerWidth <= 552) {
            objOpt.series.label.show = false;
            objOpt.yAxis.nameTextStyle = {
                fontSize: '9px'
            };
            objOpt.xAxis.axisLabel.rotate = 60;
            objOpt.yAxis.axisLabel.rotate = 50;
            objOpt.xAxis.nameTextStyle = {
                fontSize: '9px'
            };
            objOpt.tooltip.extraCssText = "max-width:90%;overflow:hidden;white-space:wrap;";
        }
        return objOpt;
    };

    formatDataPoint = (data, type) => {
        if (type == this.DATA_TYPE.DATE) {
            data = Util.getNonUTCDate(data).getTime();
        }
        return data;
    }

    formatDate = (date, format) => {
        var dt = Util.getNonUTCDate(date);
        if (!format) {
            format = "MM/dd/yyyy";
        }
        return this.$filter('date')(dt, "MM/dd/yyyy");
    }

    getStatistics = (statisticsConfig, data) => {
        var statistics = [];
        if (Array.isArray(statisticsConfig)) {
            statisticsConfig.forEach((tile) => {
                var obj, values = [], sum;
                var field = tile.calculateOnField;
                switch (tile.type.toLowerCase()) {
                    case StatisticsEnum.LATEST:
                        var nData = angular.copy(data);
                        if (nData[0][field] instanceof Date) {
                            nData = nData.sort((a, b) => {
                                if (a[field].getTime() == b[field].getTime()) {
                                    return b.index - a.index;
                                } else {
                                    return b[field].getTime() - a[field].getTime();
                                }
                            });
                            obj = nData[0];
                        }
                        break;
                    case StatisticsEnum.MAX:
                        obj = data.reduce((prev, current) => (+prev[field] > +current[field]) ? prev : current, data[0]);
                        break;
                    case StatisticsEnum.MEAN:
                        sum = data.reduce((accumulator, ob) => accumulator + +ob[field], 0);
                        break;
                    case StatisticsEnum.MIN:
                        obj = data.reduce((prev, current) => (+prev[field] < +current[field]) ? prev : current, data[0]);
                        break;
                }
                tile.labels.forEach(item => {
                    switch (item.type.toLowerCase()) {
                        case 'value':
                            values.push(item.label);
                            break;
                        case 'date':
                            if (obj[item.field]) {
                                if (item.format) {
                                    values.push(this.$filter('date')(Util.getNonUTCDate(obj[item.field]), item.format));
                                } else {
                                    values.push(this.$filter('date')(Util.getNonUTCDate(obj[item.field]), "MM/dd/yyyy"));
                                }
                            } else {
                                values.push("");
                            }
                            break;
                        case 'number':
                            if (tile.type.toLowerCase() == StatisticsEnum.MEAN) {
                                obj = {};
                                obj[item.field] = sum / data.length;
                            }
                            var value = obj[item.field];
                            if (value != null && typeof value != "undefined") {
                                if (value != 0 && item.decimal) {
                                    value = parseFloat(value + "").toFixed(item.decimal);
                                }
                                if (item.prefix) {
                                    value = item.prefix + value;
                                }
                                if (item.suffix) {
                                    value = value + item.suffix;
                                }
                                values.push(value);
                            } else {
                                values.push("");
                            }
                            break;
                    }
                });
                statistics.push({ bgColor: tile.bgColor, values: values });
            });
        }
        return statistics;
    };

    getProcessedData = (config, data) => {
        var rtnData = [];
        var fData = data;
        if (!!config.dataFilter) {
            fData = data.filter(function (d) {
                return jsonLogic.apply(config.dataFilter, d);
            });
        }
        switch (config.type) {
            case ChartTypeEnum.LINE:
                var xAxis = config.options.xAxis;
                var yAxis = config.options.yAxis;
                var setCommonProps = (d) => {
                    let xAxisVal = this.formatDataPoint(d[xAxis.field], xAxis.type);
                    let yAxisVal = this.formatDataPoint(d[yAxis.field], yAxis.type);
                    d.value = [xAxisVal, yAxisVal];
                }
                var rFields = ['index', ...config.requiredFields];
                if (Array.isArray(rFields)) {
                    fData.forEach(d => {
                        var obj = {};
                        rFields.forEach(field => {
                            obj[field] = d[field];
                        });
                        setCommonProps(obj);
                        rtnData.push(obj);
                    });
                } else {
                    fData.forEach(d => {
                        setCommonProps(d);
                        rtnData.push(d);
                    });
                }
                rtnData = rtnData.sort((a, b) => {
                    var diff = a[xAxis.field] - b[xAxis.field];
                    if (diff == 0) {
                        return a[yAxis.field] - b[yAxis.field];
                    }
                    return diff;
                });
                break;
        }
        return rtnData;
    }

    getMinMaxData = (data, field, type, min?, max?) => {
        var rtnObj = {
            min: min,
            max: max,
            data: data
        };
        var cmin, cmax;
        if (data && data.length == 0) {
            return rtnObj;
        }
        if (!min) {
            min = this.$_util.getMin(data, field);
        }
        if (!max) {
            max = this.$_util.getMax(data, field);
        }
        if (type == this.DATA_TYPE.DATE) {
            cmin = this.formatDataPoint(new Date(min).setDate(new Date(min).getDate() - this.VALUE_OFFSET), type);
            cmax = this.formatDataPoint(new Date(max).setDate(new Date(max).getDate() + this.VALUE_OFFSET), type);
        } else if (type == this.DATA_TYPE.NUMBER) {
            cmin -= this.VALUE_OFFSET;
            cmax += this.VALUE_OFFSET;
        }
        var newData = data.filter(d => {
            return (d[field] >= min && d[field] <= max);
        });
        rtnObj.data = newData;
        rtnObj.max = cmax;
        rtnObj.min = cmin;
        return rtnObj;
    }

    getUpdatedOptions = (axis, min, max, data, options, showTrendLine) => {
        options.series = {
            data: data
        }
        options[axis].min = min;
        options[axis].max = max;
        if (showTrendLine) {
            options.series.markLine = this.getTrendLineOptions(data, min, max);
        }
        return options;
    };

    setAxisLabel = (axis, obj, axisName) => {
        var objAxis = obj[axisName];
        objAxis.title = axis.label;
        objAxis.type = this.CHART_DATA_TYPES[axis.type];
        if (!objAxis.type) {
            throw ("Invalid type for axis.....");
        }
        //custom format is pending
        if (axis.format && axis.type == this.DATA_TYPE.DATE) {
            objAxis.axisLabel = {
                formatter: (value) => {
                    var dt = new Date(value);
                    return (dt.getMonth() + 1) + '/' + dt.getDate() + '/' + dt.getFullYear();
                }
            }
        } else if (axis.type == this.DATA_TYPE.NUMBER) {
            objAxis.axisLabel = {
                formatter: (value) => {
                    return axis.prefix + value + axis.suffix;
                }
            }
        }
    };

    getTrendLineOptions = (data, minX, maxX) => {
        //prepare mapped data to calculate m and b in equation y = mx + b
        var numericData = [];
        for (var i = 0; i < data.length; i++) {
            numericData.push({
                x: data[i].value[0],
                y: data[i].value[1]
            })
        }
        var a, b, c, d, minY, maxY, sumOfAllX, sumOfAllY, n, slope;
        n = numericData.length; // total points
        sumOfAllX = numericData.reduce(function (sum, obj) {
            sum += obj.x;
            return sum;
        }, 0);
        sumOfAllY = numericData.reduce(function (sum, obj) {
            sum += obj.y;
            return sum;
        }, 0);
        var signmaXMulY = numericData.reduce(function (sum, obj) {
            sum += obj.x * obj.y;
            return sum;
        }, 0);
        a = n * signmaXMulY;
        b = sumOfAllX * sumOfAllY;
        c = n * numericData.reduce(function (val, obj) {
            val += obj.x * obj.x;
            return val;
        }, 0);
        d = sumOfAllX * sumOfAllX;

        slope = (a - b) / (c - d);

        b = (sumOfAllY - slope * sumOfAllX) / n;

        minY = slope * minX + b;
        maxY = slope * maxX + b;
        if (minY < 0) {
            minY = 0;
            minX = (-b / slope);
        }
        if (maxY > 100) {
            maxY = 100;
            maxX = (100 - b) / slope;
        }

        if (maxY < 0) {
            maxY = 0;
            maxX = (-b / slope);
        }
        if (minY > 100) {
            minY = 100;
            minX = (100 - b) / slope;
        }

        return {
            silent: true,
            animation: false,
            lineStyle: {
                normal: {
                    type: 'solid',
                    color: 'green'
                }
            },
            data: [[{
                coord: [minX, minY],
                symbol: 'none'
            }, {
                coord: [maxX, maxY],
                symbol: 'none'
            }]]
        };
    };

    getDefaultLineOptions = () => {
        return {

            backgroundColor: '#fff',
            // width: 700,
            // height: 400,
            xAxis: {
                type: 'time',
                // min: '2018/10/15',
                // max: '2018/11/12',
                //interval: 7 * 24 * 3600 * 1000,
                axisLabel: {
                    formatter: function (value) {
                        var dt = new Date(value);
                        return (dt.getMonth() + 1) + '/' + dt.getDate() + '/' + dt.getFullYear();
                    }
                }
            },
            yAxis: {
                type: 'value',
                interval: 20,
                min: 0,
                max: 100,
                axisLabel: {
                    formatter: function (value) {
                        return value + '%';
                    }
                }
            },
            tooltip: {
                trigger: 'item',
                confine: true,
                borderWidth: 1,
                borderColor: '#f4b783',
                backgroundColor: '#ffd9b9',
                formatter: function (params) {
                    var rowData = params.data;
                    var dt = new Date(rowData.value[0]);
                    return "<div style='color:#222'><b>Date :</b> " + (dt.getMonth() + 1) + '/' + dt.getDate() + '/' + dt.getFullYear() + "<br />" +
                        "<b>Success : </b> " + rowData.value[1] + "% <br />" +
                        "<b>Note : </b> " + rowData.note + "</div>";
                }
            },
            series: {
                data: [],
                type: 'line',
                symbol: 'circle',
                symbolSize: 8,
                color: '#3866c8',
                smooth: true,
                label: {
                    show: true,
                    color: '#111',
                    backgroundColor: '#fff',
                    padding: 5,
                    formatter: function (value) {
                        return value.value[1] + '%';
                    }
                },
                lineStyle: {
                    width: 4,
                    color: '#11a4e7'
                }
            }
        };
    }


}

angular.module('eCareVault.common.services')
    .service('ChartService', ChartService);
